import { HHTypography } from '@hinge-health/react-component-library';
import { useCallback } from 'react';
import { useDynamicDialogContext } from '../../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import { ClientType, InsurerType } from '../../../types';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../grid-components/grid-types';
import { PanelTitle } from '../../grid-components/panel-title';
import BillDetails from '../bill-details';
import { ArchivePanel } from './archive-release-components/archive-panel';
import { CheckEligibilityPanel } from './archive-release-components/check-eligibility-panel';
import { CreateManualHoldPanel } from './archive-release-components/create-manual-hold-panel';
import { EligibilityStatusModal } from './archive-release-components/eligibility-status-modal';
import { ProcessCopaymentPanel } from './archive-release-components/process-copayment-panel';
import { ReleasePanel } from './archive-release-components/release-panel';
import { UpdateNetsuiteIdPanel } from './archive-release-components/update-netsuite-id-panel';
import { VoidPanel } from './archive-release-components/void-panel';
import {
  BillTypeDataList,
  SubmissionTypeDataList,
} from './bill-management-types';

export interface UseGridActionsConfig {
  clientsData: ClientType[];
  insurersData: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  clearAllSelections: () => void;
  gridLevel: GridLevelName;
}

export interface GridActions {
  voidSubmissions: (rows: SubmissionTypeDataList) => void;
  moreDetails: (billId: number) => void;
  archiveBill: (rows: BillTypeDataList) => void;
  releaseBill: (rows: BillTypeDataList) => void;
  checkEligibility: (rows: BillTypeDataList) => void;
  checkEligibilityStatus: () => void;
  bulkUpdateNetsuiteId: (rows: SubmissionTypeDataList) => void;
  createManualHold: (rows: BillTypeDataList) => void;
  processCopayment: (rows: BillTypeDataList) => void;
}

/**
 * Custom hook for managing grid actions (archive, release, void, etc.)
 * Grids pass in the rows they want to act on
 */
export const useGridActions = (config: UseGridActionsConfig): GridActions => {
  const { clientsData, insurersData, clearAllSelections, gridLevel } = config;

  const { setDrawerContent } = useDynamicDrawerContext();
  const { setDialogContent } = useDynamicDialogContext();

  const voidSubmissions = useCallback(
    (rows: SubmissionTypeDataList) => {
      setDrawerContent(
        <VoidPanel
          selectedRows={rows}
          onClearSelections={clearAllSelections}
        />,
        <PanelTitle
          actionType={ActionTypeDisplayText.VOID}
          rowCount={rows.length}
          gridLevel={GridLevelName.SUBMISSION}
        />,
        '50%',
        'temporary',
      );
    },
    [setDrawerContent, clearAllSelections],
  );

  const moreDetails = useCallback(
    (billId: number) => {
      setDrawerContent(
        <BillDetails key={billId} billId={billId} currency={null} />,
        <HHTypography hhVariant="section-title">Bill Details</HHTypography>,
        '30%',
        'temporary',
      );
    },
    [setDrawerContent],
  );

  const archiveBill = useCallback(
    (rows: BillTypeDataList) => {
      setDrawerContent(
        <ArchivePanel
          selectedRows={rows}
          clients={clientsData}
          insurers={insurersData}
          onClearSelections={clearAllSelections}
        />,
        <PanelTitle
          actionType={ActionTypeDisplayText.ARCHIVE}
          rowCount={rows.length}
          gridLevel={gridLevel}
        />,
        '50%',
        'temporary',
      );
    },
    [
      setDrawerContent,
      clientsData,
      insurersData,
      clearAllSelections,
      gridLevel,
    ],
  );

  const releaseBill = useCallback(
    (rows: BillTypeDataList) => {
      setDrawerContent(
        <ReleasePanel
          selectedRows={rows}
          clients={clientsData}
          insurers={insurersData}
          onClearSelections={clearAllSelections}
        />,
        <PanelTitle
          actionType={ActionTypeDisplayText.RELEASE}
          rowCount={rows.length}
          gridLevel={gridLevel}
        />,
        '50%',
        'temporary',
      );
    },
    [
      setDrawerContent,
      clientsData,
      insurersData,
      clearAllSelections,
      gridLevel,
    ],
  );

  const checkEligibility = useCallback(
    (rows: BillTypeDataList) => {
      if (rows.length > 0) {
        setDrawerContent(
          <CheckEligibilityPanel
            selectedRows={rows}
            clients={clientsData}
            insurers={insurersData}
            onClearSelections={clearAllSelections}
          />,
          <PanelTitle
            actionType={ActionTypeDisplayText.CHECK_ELIGIBILITY}
            rowCount={rows.length}
            gridLevel={gridLevel}
          />,
          '50%',
          'temporary',
        );
      }
    },
    [
      setDrawerContent,
      clientsData,
      insurersData,
      clearAllSelections,
      gridLevel,
    ],
  );

  const checkEligibilityStatus = useCallback(() => {
    setDialogContent({
      title: (
        <HHTypography hhVariant="section-title">
          Status of Eligibility Checks
        </HHTypography>
      ),
      content: <EligibilityStatusModal />,
      fullWidth: true,
      width: 'md',
      footer: (
        <HHTypography hhVariant="muted">
          <i>* This data is saved in your browser local storage</i>
        </HHTypography>
      ),
    });
  }, [setDialogContent]);

  const bulkUpdateNetsuiteId = useCallback(
    (rows: SubmissionTypeDataList) => {
      setDrawerContent(
        <UpdateNetsuiteIdPanel
          selectedRows={rows}
          onClearSelections={clearAllSelections}
        />,
        <PanelTitle
          actionType={ActionTypeDisplayText.UPDATE_NETSUITE_ID}
          rowCount={rows.length}
          gridLevel={GridLevelName.SUBMISSION}
        />,
        '50%',
        'temporary',
      );
    },
    [setDrawerContent, clearAllSelections],
  );

  const createManualHold = useCallback(
    (rows: BillTypeDataList) => {
      setDrawerContent(
        <CreateManualHoldPanel
          selectedRows={rows}
          clients={clientsData}
          insurers={insurersData}
          onClearSelections={clearAllSelections}
        />,
        <PanelTitle
          actionType={ActionTypeDisplayText.CREATE_MANUAL_HOLD}
          rowCount={rows.length}
          gridLevel={gridLevel}
        />,
        '50%',
        'temporary',
      );
    },
    [
      setDrawerContent,
      clientsData,
      insurersData,
      clearAllSelections,
      gridLevel,
    ],
  );

  const processCopayment = useCallback(
    (rows: BillTypeDataList) => {
      setDrawerContent(
        <ProcessCopaymentPanel
          selectedRows={rows}
          clients={clientsData}
          insurers={insurersData}
          onClearSelections={clearAllSelections}
        />,
        <PanelTitle
          actionType={ActionTypeDisplayText.PROCESS_COPAYMENT}
          rowCount={rows.length}
          gridLevel={gridLevel}
        />,
        '50%',
        'temporary',
      );
    },
    [
      setDrawerContent,
      clientsData,
      insurersData,
      clearAllSelections,
      gridLevel,
    ],
  );

  return {
    voidSubmissions,
    moreDetails,
    archiveBill,
    releaseBill,
    checkEligibility,
    checkEligibilityStatus,
    bulkUpdateNetsuiteId,
    createManualHold,
    processCopayment,
  };
};
